/**
 * Composite rooms API for playground mode.
 *
 * Combines HttpRoomsApi (lifecycle operations) with FirestoreRoomsApi (data/subscriptions).
 * This matches how the mobile app works - HTTP for server-managed operations,
 * direct Firestore for data and real-time subscriptions.
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
 * Do NOT import from barrel exports (rooms/index.ts) - use direct import path instead.
 * This ensures Firebase is not bundled in production builds.
 */
import {
  RoomsApi,
  CreateRoomOptions,
  JoinOrCreateRoomOptions,
  JoinOrCreateResult,
  ListRoomsOptions,
  RoomSubscriptionOptions,
  UpdateRoomDataOptions,
  RoomMessageRequest,
  StartRoomGameOptions,
  ProposeMoveRequest,
  ProposeMoveResult,
  ValidateMoveVerdict,
  ValidateMoveResult,
} from './RoomsApi'
import { RundotGameRoom } from './RundotGameRoom'
import { HttpRoomsApi } from './HttpRoomsApi'
import { FirestoreRoomsApi } from './FirestoreRoomsApi'

/**
 * Composite implementation that delegates to HTTP or Firestore as appropriate.
 */
export class CompositeRemoteRoomsApi implements RoomsApi {
  private readonly httpApi: HttpRoomsApi
  private readonly firestoreApi: FirestoreRoomsApi

  constructor(httpApi: HttpRoomsApi, firestoreApi: FirestoreRoomsApi) {
    this.httpApi = httpApi
    this.firestoreApi = firestoreApi
  }

  // ===== HTTP-backed lifecycle operations =====

  async createRoomAsync(options: CreateRoomOptions): Promise<RundotGameRoom> {
    return this.httpApi.createRoomAsync(options)
  }

  async joinOrCreateRoomAsync(options: JoinOrCreateRoomOptions): Promise<JoinOrCreateResult> {
    return this.httpApi.joinOrCreateRoomAsync(options)
  }

  async joinRoomByCodeAsync(roomCode: string): Promise<RundotGameRoom> {
    return this.httpApi.joinRoomByCodeAsync(roomCode)
  }

  async getUserRoomsAsync(options?: ListRoomsOptions): Promise<RundotGameRoom[]> {
    return this.httpApi.getUserRoomsAsync(options)
  }

  async leaveRoomAsync(room: RundotGameRoom): Promise<void> {
    return this.httpApi.leaveRoomAsync(room)
  }

  async kickPlayerAsync(
    room: RundotGameRoom,
    targetProfileId: string,
    options?: { reason?: string },
  ): Promise<void> {
    return this.httpApi.kickPlayerAsync(room, targetProfileId, options)
  }

  async startRoomGameAsync(room: RundotGameRoom, options?: StartRoomGameOptions): Promise<void> {
    return this.httpApi.startRoomGameAsync(room, options)
  }

  // ===== Firestore-backed data operations =====

  async subscribeAsync(room: RundotGameRoom, options?: RoomSubscriptionOptions): Promise<() => void> {
    return this.firestoreApi.subscribeAsync(room, options)
  }

  async updateRoomDataAsync(
    room: RundotGameRoom,
    updates: Record<string, unknown>,
    options?: UpdateRoomDataOptions,
  ): Promise<void> {
    return this.firestoreApi.updateRoomDataAsync(room, updates, options)
  }

  async getRoomDataAsync(room: RundotGameRoom): Promise<Record<string, unknown>> {
    return this.firestoreApi.getRoomDataAsync(room)
  }

  async sendRoomMessageAsync(room: RundotGameRoom, message: RoomMessageRequest): Promise<string> {
    return this.firestoreApi.sendRoomMessageAsync(room, message)
  }

  async proposeMoveAsync(room: RundotGameRoom, request: ProposeMoveRequest): Promise<ProposeMoveResult> {
    return this.firestoreApi.proposeMoveAsync(room, request)
  }

  async validateMoveAsync(
    room: RundotGameRoom,
    moveId: string,
    verdict: ValidateMoveVerdict,
  ): Promise<ValidateMoveResult> {
    return this.firestoreApi.validateMoveAsync(room, moveId, verdict)
  }

  /**
   * Clean up all active subscriptions.
   */
  destroy(): void {
    this.firestoreApi.destroy()
  }
}
